import logo from '../images/icon_small.png';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import '../style/parallax-star.css';
import '../style/Home.css';
import { IoPeople, IoGridOutline, IoNotificationsOutline, IoSettings, IoLogOutOutline, IoShieldCheckmarkOutline } from "react-icons/io5";
import { logout as endSession } from '../api/TokenOperations';
import { getMyRole } from '../utils/jwt';
import { useConfirm } from '../hooks/useConfirm';

import {
  Redirect,
  Route
} from "react-router-dom";

import Chat from "../pages/Chat";
import Friends from "../pages/Friends";
import Groups from "../pages/Groups";
import Notifications from "../pages/Notifications";
import Settings from '../pages/Settings';
import { SocketProvider, useSocket } from '../socket/SocketContext';
import { ProfileProvider } from '../profile/ProfileContext';
import ToastContainer from '../components/ToastContainer';
import VoiceRoomPanel from '../components/VoiceRoomPanel';
import KnockOverlay from '../components/KnockOverlay';
import { useLanguage } from '../i18n/LanguageContext';

const NAV_ITEM_SIZE = 48;
const NAV_ITEM_GAP = 14;

const NAV_ROUTES = ["/home/", "/home/friends", "/home/groups", "/home/notifications", "/home/settings"];

// `label` + `extraClass` only matter in the phone layout (see
// style/mobile.css): the label (a short form of `title` - five full names
// don't fit a 320px bar) shows under the icon in the portrait bottom bar,
// since a tooltip `title` means nothing on a touchscreen; NaviSecondary
// items (admin, logout) move out of the bar into Settings there. Neither
// changes anything on desktop: the label is display:none outside the phone
// layout and NaviSecondary has no desktop styles.
function NaviIconButton({ icon, badge, onClick, title, label, active, extraClass = '' }) {
  return (
    <button className={`NaviButton${active ? ' NaviButtonActive' : ''}${extraClass ? ` ${extraClass}` : ''}`} onClick={onClick} title={title} style={{ position: 'relative', zIndex: 1 }}>
      {icon}
      {label && <span className="NaviLabel">{label}</span>}
      {badge > 0 && <span className="IconBadge">{badge > 9 ? '9+' : badge}</span>}
    </button>
  );
}

function HomeContent({ pageActive, history, logout }) {
  const { unreadFriendRequests, unreadGroupInvites, unreadMessageCounts, notifications, groupState } = useSocket();
  const { t } = useLanguage();
  const [confirm, confirmEl] = useConfirm();
  const askLogout = () => confirm({ message: t('settings.logoutConfirm'), confirmLabel: t('nav.logout'), onConfirm: logout });

  const unreadMessages = Object.keys(unreadMessageCounts).length;
  const unreadNotifications = notifications.filter((n) => !n.read).length;
  // The voice panel floats fixed over everything (screens/Home.js renders it
  // as a sibling below) - without this, it visually covers whatever's
  // underneath (chat messages/input, a group's header buttons, ...) instead
  // of the page making room for it. See VoicePanelActive in Home.css.
  const voiceActive = !!groupState.currentRoomId;

  let activeIndex = NAV_ROUTES.indexOf(pageActive);
  if (activeIndex === -1 && pageActive === "/home") activeIndex = 0;
  if (activeIndex === -1) activeIndex = 0;

  return (
    <div className="parallax-back App-Home-Back">
      {/* Electron-only (electron-starter.js hides the native title bar via
          titleBarStyle:'hidden' so it can match the app's own background -
          traffic lights still render natively, but window dragging needs an
          explicit region since there's no OS title bar left to provide one).
          Gated on window.cyclone (preload.js) so the plain web build never
          gets an invisible click-blocking strip across its top edge. */}
      {window.cyclone && <div className="TitleBarDragRegion" />}
      <div className={`App-Home${voiceActive ? ' VoicePanelActive' : ''}`}>
        <div className="Navigator glass">
          <div className="NavIconList">
            <div className="NavIndicator" style={{ transform: `translateY(${activeIndex * (NAV_ITEM_SIZE + NAV_ITEM_GAP)}px)` }}></div>
            <NaviIconButton icon={<img src={logo} className="App-H-Logo-Icon" alt={t('nav.chats')} />} badge={unreadMessages} onClick={() => history.push("/home/")} title={t('nav.chats')} label={t('navShort.chats')} active={activeIndex === 0} />
            <NaviIconButton icon={<IoPeople />} badge={unreadFriendRequests} onClick={() => history.push("/home/friends")} title={t('nav.friends')} label={t('navShort.friends')} active={activeIndex === 1} />
            <NaviIconButton icon={<IoGridOutline />} badge={unreadGroupInvites} onClick={() => history.push("/home/groups")} title={t('nav.groups')} label={t('navShort.groups')} active={activeIndex === 2} />
            <NaviIconButton icon={<IoNotificationsOutline />} badge={unreadNotifications} onClick={() => history.push("/home/notifications")} title={t('nav.notifications')} label={t('navShort.notifications')} active={activeIndex === 3} />
            <NaviIconButton icon={<IoSettings />} onClick={() => history.push("/home/settings")} title={t('nav.settings')} label={t('navShort.settings')} active={activeIndex === 4} />
          </div>
          {getMyRole() === 'admin' && (
            <NaviIconButton icon={<IoShieldCheckmarkOutline />} onClick={() => history.push("/admin")} title={t('nav.admin')} extraClass="NaviSecondary" />
          )}
          <button className="NaviButton NaviSecondary" onClick={askLogout} title={t('nav.logout')}><IoLogOutOutline/></button>
        </div>
        <Route exact path={"/home/"} component={Chat} />
        <Route path={"/home/friends"} component={Friends} />
        {/* Search lives in Friends now - old links still land there. */}
        <Route path={"/home/search"}><Redirect to="/home/friends" /></Route>
        <Route path={"/home/groups"} component={Groups} />
        <Route path={"/home/notifications"} component={Notifications} />
        <Route path={"/home/settings"} component={Settings} />
        <ToastContainer />
        <VoiceRoomPanel />
        <KnockOverlay />
        {confirmEl}
      </div>
      <div id='stars'></div>
      <div id='stars2'></div>
      <div id='stars3'></div>
    </div>
  );
}

export default function Home({children}) {
  const history = useHistory();

  const [pageActive, setActivePage] = useState("/home/");
  let location = useLocation();

  useEffect(() => {
    if(!localStorage.getItem("accessToken")){
      history.replace("/");
    }
    setActivePage(location.pathname);
  }, [history, location]);

  const logout = () => {
    endSession();
    history.replace("/");
  }

  return (
      <SocketProvider>
        <ProfileProvider>
          <HomeContent pageActive={pageActive} history={history} logout={logout} />
        </ProfileProvider>
      </SocketProvider>
  );
}
